import React from 'react';
import { Clock } from 'lucide-react';

export function NeededItems() {
  return (
    <section className="py-16 bg-white dark:bg-accessible-gray-800 high-contrast:bg-black">
      <div className="max-w-7xl mx-auto px-8 md:px-12">
        <div className="text-center mb-12">
          <h2 className="section-title mb-4">
            Jak możesz pomóc?
          </h2>
          <p className="body-large max-w-3xl mx-auto">
            Nie możesz przygarnąć zwierzaka? Jest wiele innych sposobów, by pomóc naszym podopiecznym.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Ways to Help */}
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-8 rounded-lg">
            <div className="mb-6">
              <h3 className="card-title">
                Sposoby pomocy
              </h3>
            </div>

            <div className="prose prose-gray dark:prose-invert max-w-none">
              <p className="body-medium mb-4">
                Każda forma pomocy jest dla nas cenna. Nawet jeśli nie możesz adoptować zwierzaka, jest wiele innych sposobów, by wesprzeć nasze działania:
              </p>
              <ul className="space-y-4 body-medium">
                <li className="list-item">
                  <strong className="label-large">Adopcja wirtualna</strong>
                  <p className="mt-1 body-medium">Wesprzyj konkretnego zwierzaka poprzez regularne wpłaty lub zakup karmy.</p>
                </li>
                <li className="list-item">
                  <strong className="label-large">Wolontariat zdalny</strong>
                  <p className="mt-1 body-medium">Pomóż nam w prowadzeniu social mediów lub publikowaniu ogłoszeń adopcyjnych.</p>
                </li>
                <li className="list-item">
                  <strong className="label-large">Wsparcie materialne</strong>
                  <p className="mt-1 body-medium">Przekaż karmę, środki czystości lub inne potrzebne rzeczy z naszej listy.</p>
                </li>
              </ul>
              <p className="body-medium font-medium mt-6">
                Każda, nawet najmniejsza pomoc, ma znaczenie. Razem możemy zrobić więcej dla naszych podopiecznych!
              </p>
            </div>
          </div>

          {/* Current Needs */}
          <div className="bg-accessible-gray-50 dark:bg-accessible-gray-700 high-contrast:bg-black p-8 rounded-lg">
            <div className="mb-6">
              <h3 className="card-title">
                Lista aktualnych potrzeb
              </h3>
            </div>


            <div className="space-y-6">
              <div>
                <h4 className="heading-5 mb-3">
                  Karma dla psów
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-4 rounded-lg">
                    <p className="label-large mb-2">Karma sucha:</p>
                    <ul className="list-disc pl-4 body-medium space-y-1">
                      <li className="body-medium no-margin">Bosh</li>
                      <li className="body-medium no-margin">Britt</li>
                      <li className="body-medium no-margin">Josera</li>
                      <li className="body-medium no-margin">Husse</li>
                      <li className="body-medium no-margin">Karmy weterynaryjne</li>
                    </ul>
                  </div>
                  <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-4 rounded-lg">
                    <p className="label-large mb-2">Karma mokra:</p>
                    <ul className="list-disc pl-4 body-medium space-y-1">
                      <li className="body-medium no-margin">Rocco</li>
                      <li className="body-medium no-margin">Dolina Noteci</li>
                      <li className="body-medium no-margin">Animonda Gran Carno</li>
                      <li className="body-medium no-margin">Lukullus</li>
                      <li className="body-medium no-margin">Karmy weterynaryjne</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-3">
                  Karma dla kotów
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-4 rounded-lg">
                    <p className="label-large mb-2">Karma sucha:</p>
                    <ul className="list-disc pl-4 body-medium space-y-1">
                      <li className="body-medium no-margin">Feringa</li>
                      <li className="body-medium no-margin">Smilla</li>
                      <li className="body-medium no-margin">Purizon</li>
                      <li className="body-medium no-margin">Britt</li>
                      <li className="body-medium no-margin">Karmy weterynaryjne (gastro intestinal, urinary)</li>
                    </ul>
                  </div>
                  <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-4 rounded-lg">
                    <p className="label-large mb-2">Karma mokra:</p>
                    <ul className="list-disc pl-4 body-medium space-y-1">
                      <li className="body-medium no-margin">Animonda Carny</li>
                      <li className="body-medium no-margin">Smilla</li>
                      <li className="body-medium no-margin">Feringa</li>
                      <li className="body-medium no-margin">Grau</li>
                      <li className="body-medium no-margin">Catz Finefood</li>
                      <li className="body-medium no-margin">Wild Freedom</li>
                      <li className="body-medium no-margin">Macs</li>
                      <li className="body-medium no-margin">Karmy weterynaryjne (gastro intestinal, urinary)</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-accessible-gray-900 dark:text-white high-contrast:text-white mb-3">
                  Pozostałe potrzeby
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-4 rounded-lg">
                    <p className="label-large mb-2">Akcesoria:</p>
                    <ul className="list-disc pl-4 body-medium space-y-1">
                      <li className="body-medium no-margin">Zabawki (twarde gryzaki)</li>
                      <li className="body-medium no-margin">Smycze i obroże</li>
                      <li className="body-medium no-margin">Transporterki dla kotów</li>
                      <li className="body-medium no-margin">Kuwety</li>
                      <li className="body-medium no-margin">Koce i ręczniki</li>
                      <li className="body-medium no-margin">Żwirek dla kotów</li>
                    </ul>
                  </div>
                  <div className="bg-white dark:bg-accessible-gray-800 high-contrast:bg-black p-4 rounded-lg">
                    <p className="label-large mb-2">Higiena i zdrowie:</p>
                    <ul className="list-disc pl-4 body-medium space-y-1">
                      <li className="body-medium no-margin">Probiotyki (Purina Pro Plan, Fortiflora)</li>
                      <li className="body-medium no-margin">Podkłady</li>
                      <li className="body-medium no-margin">Ręczniki papierowe</li>
                      <li className="body-medium no-margin">Rękawiczki jednorazowe</li>
                      <li className="body-medium no-margin">Wirkon</li>
                      <li className="body-medium no-margin">Proszek do prania</li>
                      <li className="body-medium no-margin">Płyny do sprzątania</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-accessible-red-600/10 p-6 rounded-lg mt-8">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-5 h-5 text-accessible-red-600" aria-hidden="true" />
                  <h4 className="heading-5 no-margin">
                    Chcesz przekazać dary?
                  </h4>
                </div>
                <p className="body-medium mb-4">
                  Jeśli chciałbyś przekazać jakieś inne dary lub pomóc w inny sposób, prosimy o kontakt:
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="tel:+48664821160" className="link-primary">
                    tel. 664 821 160
                  </a>
                  <a href="mailto:znajdki2012@gmail.com" className="link-primary">
                    znajdki2012@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}